import api from './api';
import type { Paginated, Trip, TripStatus, TripSearchResult, BulkGeneratePayload, BulkGenerateResult } from '../types/api';

/**
 * Whether a trip is still relevant to travellers: not cancelled or completed,
 * and not departed on an earlier day. Seat availability is ignored, so this
 * keeps sold-out (but upcoming) trips visible in browse lists.
 *
 * Trip status is not auto-advanced when departure passes, so a stale
 * `scheduled`/`boarding` trip would otherwise linger. We hide any trip whose
 * departure day is before today (matching the "Past" bucket on the trips
 * screens); same-day trips stay visible so late/downstream boarding still works.
 */
export function isUpcomingTrip(
  t: Pick<Trip, 'status' | 'departure_time'>,
  now: Date = new Date(),
): boolean {
  if (t.status === 'cancelled' || t.status === 'completed') return false;
  if (!t.departure_time) return true;
  const dep = new Date(t.departure_time);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return dep.getTime() >= startOfToday.getTime();
}

/**
 * Whether a trip should be offered in a "New booking" picker: upcoming (see
 * {@link isUpcomingTrip}) and with at least one free seat.
 */
export function isBookableTrip(
  t: Pick<Trip, 'status' | 'available_seats' | 'departure_time'>,
  now: Date = new Date(),
): boolean {
  if (typeof t.available_seats === 'number' && t.available_seats <= 0) return false;
  return isUpcomingTrip(t, now);
}

export async function listTrips(params?: Record<string, string | number>) {
  const { data } = await api.get<Paginated<Trip> | Trip[]>('/trips/', { params });
  return Array.isArray(data) ? data : data.results;
}

export async function createTrip(payload: Partial<Trip>) {
  const { data } = await api.post<Trip>('/trips/', payload);
  return data;
}

export async function updateTrip(id: number, payload: Partial<Trip>) {
  const { data } = await api.patch<Trip>(`/trips/${id}/`, payload);
  return data;
}

export async function setTripStatus(id: number, status: TripStatus) {
  await api.post(`/trips/${id}/update_status/`, { status });
}

export async function deleteTrip(id: number) {
  await api.delete(`/trips/${id}/`);
}

export async function searchTrips(params: { from: number; to: number; date: string }) {
  const { data } = await api.get<TripSearchResult[]>('/trips/search/', { params });
  return data;
}

export async function getTripDetail(id: number) {
  const { data } = await api.get<Trip>(`/trips/${id}/`);
  return data;
}

export async function setTripFares(
  id: number,
  fares: { boarding_stop_id: number; alighting_stop_id: number; amount: string }[],
) {
  const { data } = await api.post<{ detail: string; is_fare_locked: boolean }>(
    `/trips/${id}/set_trip_fares/`,
    { fares },
  );
  return data;
}

export async function bulkGenerateTrips(payload: BulkGeneratePayload): Promise<BulkGenerateResult> {
  const { data } = await api.post<BulkGenerateResult>('/trips/bulk_generate/', payload);
  return data;
}
